import React from 'react'
import { Trans } from 'react-i18next'
import i18next from 'i18next'
import classNames from 'classnames'
import type { Maybe } from '@zaptic-external/ts-monads'
import type { ReportGuideDetails, ReportGuideId, SyncGuide } from '../../../shared/domain/guides'
import type { FileId } from '../../../shared/domain/files'
import type { FileExtension } from '../../../shared/domain/files/fileExtensions.no-deps'
import type { Nullable } from '../../../shared/lib/typeHelpers.no-deps'
import { CenteredMessage } from '../../../shared/webcomponents/react-wrappers/CenteredMessage'
import type { CommonDeps } from '../base/CommonDeps'
import { ZapBotModalPage } from './ZapBot'
import type { AppFileGuideDetails, AppGuide, ParentInfo } from '../guide/Model'
import type { RenderCachedImage } from '../image/WithCachedImage'
import { GuidePresentation } from '../guide/GuidePresentation'
import { createGuideExecution } from '../guide/helpers'
import { SourceList } from './ZapBotSourceList'
import { ZapBotAnswerRenderer } from './ZapBotAnswerRenderer'
import { CREDITS_COST, type CreditBalance } from '../../../shared/domain/credits/types.no-deps'
import { CreditBalanceBox } from '../creditBalance/CreditBalanceBox'
import type { IconName } from '../../../shared/webcomponents/helpers/icons'
import { ZapBotContext } from './ZapBotContext'
import type { CamelCaseFlags } from '../../../shared/cfg/flags.no-deps'
import {
    type AiChatSource,
    type AiChatGuideSource,
    isAiChatGuideSource,
} from '../../../shared/domain/aiChats/types.no-deps'

type FeedbackState = {
    isGood?: boolean
    note: string
    hasSubmittedRating: boolean
    hasSubmittedNote: boolean
}

export function ZapBotModalBody(props: {
    deps: CommonDeps
    allowImageZoom: boolean
    featureFlags: Pick<CamelCaseFlags, 'aiCredits'>
    fetchAppGuides: (guideSources: AiChatGuideSource[], withParentInfo: boolean) => Promise<AppGuide[]>
    fetchFileGuideDetails: (fileId: FileId) => Promise<AppFileGuideDetails>
    fetchReportGuideDetails: (reportGuideId: ReportGuideId) => Promise<ReportGuideDetails>
    fetchGuideParentInfo: (guideSource: AiChatGuideSource) => Promise<ParentInfo[]>
    maybeGuideToExecute: Maybe<AiChatGuideSource>
    renderCachedImage: RenderCachedImage
    renderWorkflowGuideExecution: (guide: SyncGuide<'workflow'>) => React.ReactElement<unknown>
    onExecutionDone: () => void
    onSourceClick: (source: AiChatSource) => void
    onClickGuideParentInfo: (parentInfo: ParentInfo) => void
    onOpenFile: (downloadUrl: string, fileExtension: FileExtension, name: string) => Promise<void>
    onOpenFileDone: () => void
    onSubmitFeedback: (isGood: boolean, note: string) => Promise<void>
    allowAnswerCopy?: boolean
}) {
    const { creditBalance, goBack, goToPage, hasCredits, isLoading, page, promptAnswer } =
        React.useContext(ZapBotContext)

    const {
        deps,
        allowImageZoom,
        featureFlags,
        fetchAppGuides,
        fetchFileGuideDetails,
        fetchReportGuideDetails,
        fetchGuideParentInfo,
        maybeGuideToExecute,
        renderWorkflowGuideExecution,
        onExecutionDone,
        onSourceClick,
        onClickGuideParentInfo,
        onOpenFile,
        onOpenFileDone,
        renderCachedImage,
        onSubmitFeedback,
        allowAnswerCopy,
    } = props

    const [feedbackState, setFeedbackState] = React.useState<FeedbackState>({
        note: '',
        isGood: null,
        hasSubmittedRating: false,
        hasSubmittedNote: false,
    })

    const answerRef = React.useRef(null)

    const handleOnOpenFile = (guide: SyncGuide, downloadUrl: string, fileExtension: FileExtension, name: string) => {
        const pdfSource =
            fileExtension === '.pdf' &&
            promptAnswer.sources.filter(isAiChatGuideSource).find((entry) => entry.guide.id === guide.id)

        if (pdfSource && pdfSource.pages?.length) {
            const [firstPage] = pdfSource.pages

            return onOpenFile(`${downloadUrl}#page=${firstPage.number}`, fileExtension, name).then(onOpenFileDone)
        }

        return onOpenFile(downloadUrl, fileExtension, name).then(onOpenFileDone)
    }

    const handleOnSourceClick = (source: AiChatSource, downloadUrl?: string) => {
        if (source.type === 'chatFile') {
            const [firstPage] = source.pages || []

            return onOpenFile(
                `${downloadUrl}#page=${firstPage?.number ?? 1}`,
                source.file.extension,
                source.file.fileName
            ).then(onOpenFileDone)
        }

        return onSourceClick(source)
    }

    const onClickFeedbackButton = (isGood: boolean) => {
        // Cancel the submission if clicking on the same button twice.
        const newValue = feedbackState.isGood === isGood ? null : isGood

        setFeedbackState((state) => ({
            ...state,
            hasSubmittedRating: newValue !== null,
            hasSubmittedNote: false,
            isGood: newValue,
        }))

        onSubmitFeedback(newValue, feedbackState.note)
    }

    const isOnPrivacyPolicyPage = page === ZapBotModalPage.PrivacyPolicy
    const isOnCreditsInfoPage = page === ZapBotModalPage.CreditsInfo

    const onClickPrivacyPolicyButton = () =>
        goToPage(page !== ZapBotModalPage.PrivacyPolicy ? ZapBotModalPage.PrivacyPolicy : ZapBotModalPage.Main)

    const onChangeFeedbackNote = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setFeedbackState((state) => ({ ...state, note: event.target.value }))
    }

    const onClickSubmitButton = async () => {
        await props.onSubmitFeedback(feedbackState.isGood, feedbackState.note)

        setFeedbackState((state) => ({ ...state, hasSubmittedNote: true }))
    }

    const onClickCopyButton = () => {
        if (!answerRef) {
            return
        }
        navigator.clipboard.writeText(answerRef.current.innerText)
    }

    if (isOnPrivacyPolicyPage) {
        return <PrivacyPolicyPage onClickBackButton={() => goBack()} />
    }

    if (isOnCreditsInfoPage) {
        return <CreditsInfoPage creditBalance={creditBalance} onClickPrivacyPolicyButton={onClickPrivacyPolicyButton} />
    }

    const guideSourceToPresent: Nullable<AiChatGuideSource> = maybeGuideToExecute.withDefault(null)
    if (guideSourceToPresent) {
        return (
            <GuidePresentation
                deps={deps}
                allowImageZoom={allowImageZoom}
                fetchGuideExecution={() =>
                    createGuideExecution(
                        guideSourceToPresent.guide,
                        fetchFileGuideDetails,
                        () => fetchGuideParentInfo(guideSourceToPresent),
                        fetchReportGuideDetails
                    )
                }
                renderCachedImage={renderCachedImage}
                renderWorkflowGuideExecution={renderWorkflowGuideExecution}
                onClickGuideParentInfo={onClickGuideParentInfo}
                onBack={onExecutionDone}
                onOpenFile={(...args) => handleOnOpenFile(guideSourceToPresent.guide, ...args)}
            />
        )
    }

    if (promptAnswer.hasError) {
        return (
            <ErrorStateView
                iconName="spilled-coffee-cup"
                heading={i18next.t('appcore:ZapBot.errorStateHeading')}
                message={i18next.t('appcore:ZapBot.errorStateDescription')}
            />
        )
    }

    if (featureFlags.aiCredits && !hasCredits && !promptAnswer.promptId) {
        return (
            <>
                <ErrorStateView
                    iconName="empty-wallet"
                    className="ZapBot_out-of-credits-state"
                    heading={i18next.t('appcore:ZapBot.outOfCreditsStateHeading')}
                    message={i18next.t('appcore:ZapBot.outOfCreditsStateMsg')}
                    testId="zapbot-no-credits-error"
                />

                <Footer onClickPrivacyPolicyButton={onClickPrivacyPolicyButton} showUserTermsLink />
            </>
        )
    }

    if (!promptAnswer.promptId && !isLoading) {
        return <EmptyStateView onClickPrivacyPolicyButton={onClickPrivacyPolicyButton} />
    }

    const isWarningShown = promptAnswer.sources.find((entry) => entry.type === 'report')
    return (
        <>
            {promptAnswer.predefinedAnswer ? (
                <div className="ZapBot_answer" data-testid="zapbot-answer">
                    {i18next.t('appcore:ZapBot.unknownResponse')}
                </div>
            ) : (
                <div
                    ref={answerRef}
                    className={classNames('ZapBot_answer', { 'ZapBot_answer--loading': isLoading })}
                    data-testid="zapbot-answer"
                >
                    <ZapBotAnswerRenderer
                        content={promptAnswer.text}
                        sources={promptAnswer.sources}
                        isLoading={isLoading}
                        allowAnswerCopy={allowAnswerCopy}
                        onClickCopyButton={onClickCopyButton}
                    />
                </div>
            )}
            {promptAnswer.sources.length > 0 && (
                <div>
                    <div className="ZapBot_sources-header">
                        <span className="ZapBot_sources-heading">{i18next.t('appcore:ZapBot.sources')}</span>
                        {isWarningShown && (
                            <span className="ZapBot_sources-warning-icon">
                                <zaptic-icon icon-name="warning-triangle"></zaptic-icon>
                            </span>
                        )}
                    </div>
                    <p className="ZapBot_sources-warning-text">{i18next.t('appcore:ZapBot.reportSourcesWarning')}</p>
                    <SourceList
                        sources={promptAnswer.sources}
                        onSourceClick={handleOnSourceClick}
                        onClickGuideParentInfo={onClickGuideParentInfo}
                        deps={deps}
                        renderCachedImage={renderCachedImage}
                        fetchAppGuides={fetchAppGuides}
                    />
                </div>
            )}

            {!isLoading && (
                <>
                    <div className="ZapBot_feedback">
                        <div className="ZapBot_feedback-header">
                            <button
                                className={classNames('ZapBot_feedback-button down', {
                                    active: feedbackState.isGood === false,
                                    'greyed-out': feedbackState.isGood === true,
                                })}
                                onClick={() => onClickFeedbackButton(false)}
                            >
                                <zaptic-icon icon-name="thumbs-up" />
                            </button>

                            <span className="ZapBot_feedback-message">
                                {feedbackState.hasSubmittedRating &&
                                    feedbackState.hasSubmittedNote &&
                                    i18next.t('appcore:ZapBot.feedbackSubmittedMsg')}

                                {!feedbackState.hasSubmittedNote && feedbackState.hasSubmittedRating && (
                                    <span>
                                        {feedbackState.isGood
                                            ? i18next.t('appcore:ZapBot.positiveFeedbackMsg')
                                            : i18next.t('appcore:ZapBot.negativeFeedbackMsg')}
                                    </span>
                                )}

                                {!feedbackState.hasSubmittedNote &&
                                    !feedbackState.hasSubmittedRating &&
                                    i18next.t('appcore:ZapBot.pendingFeedbackMsg')}
                            </span>

                            <button
                                className={classNames('ZapBot_feedback-button up', {
                                    active: feedbackState.isGood === true,
                                    'greyed-out': feedbackState.isGood === false,
                                })}
                                onClick={() => onClickFeedbackButton(true)}
                            >
                                <zaptic-icon icon-name="thumbs-up" />
                            </button>
                        </div>

                        {feedbackState.hasSubmittedRating && !feedbackState.hasSubmittedNote && (
                            <div className="ZapBot_feedback-note">
                                <label htmlFor="feedback-note" className="ZapBot_feedback-note-label">
                                    <Trans i18nKey="appcore:ZapBot.feedbackInputLabel">
                                        <span className="ZapBot_feedback-note-label--light"></span>
                                    </Trans>
                                </label>

                                <textarea
                                    id="feedback-note"
                                    className="ZapBot_feedback-note-input"
                                    value={feedbackState.note}
                                    placeholder={i18next.t(
                                        feedbackState.isGood
                                            ? 'appcore:ZapBot.positiveInputPlaceholder'
                                            : 'appcore:ZapBot.negativeInputPlaceholder'
                                    )}
                                    onChange={onChangeFeedbackNote}
                                />

                                <div className="ZapBot_feedback-cta-buttons">
                                    <zaptic-button
                                        type="primary"
                                        size="small"
                                        className="ZapBot_feedback-submit-button"
                                        disabled={!feedbackState.note.length}
                                        onClick={onClickSubmitButton}
                                    >
                                        {i18next.t('appcore:common.submit')}
                                    </zaptic-button>
                                </div>
                            </div>
                        )}
                    </div>

                    <Footer onClickPrivacyPolicyButton={onClickPrivacyPolicyButton} showUserTermsLink={false} />
                </>
            )}
        </>
    )
}

type EmptyStateViewProps = {
    onClickPrivacyPolicyButton: () => void
}

function EmptyStateView({ onClickPrivacyPolicyButton }: EmptyStateViewProps) {
    return (
        <>
            <div className="ZapBot_centered-message">
                <CenteredMessage iconName="zapbot-full" message="appcore:ZapBot.emptyStateDescription" />
            </div>

            <Footer onClickPrivacyPolicyButton={onClickPrivacyPolicyButton} showUserTermsLink />
        </>
    )
}

type ErrorStateViewProps = {
    iconName: IconName
    className?: string
    heading: string
    message: string
    testId?: string
}

function ErrorStateView({ className, iconName, heading, message, testId }: ErrorStateViewProps) {
    return (
        <div className={`ZapBot_centered-message ${className}`} {...(testId && { 'data-testid': testId })}>
            <CenteredMessage iconName={iconName} heading={heading} message={message} />
        </div>
    )
}

type FooterProps = {
    onClickPrivacyPolicyButton: () => void
    showUserTermsLink: boolean
}

function Footer({ onClickPrivacyPolicyButton, showUserTermsLink }: FooterProps) {
    return (
        <div className="ZapBot_footer">
            <hr className="ZapBot_footer-hr" />

            <p className="ZapBot_disclaimer-text">
                <Trans i18nKey="appcore:ZapBot.disclaimer">
                    <zaptic-button
                        type="link"
                        className="ZapBot_disclaimer-link-button"
                        onClick={onClickPrivacyPolicyButton}
                    />
                </Trans>
            </p>

            {showUserTermsLink && (
                <a className="ZapBot_user-terms-link" href="https://zaptic.com/privacy" target="_blank">
                    <Trans i18nKey="appcore:ZapBot.userTermsLink" />
                </a>
            )}
        </div>
    )
}

type PrivacyPolicyPageProps = {
    onClickBackButton: () => void
}

function PrivacyPolicyPage({ onClickBackButton }: PrivacyPolicyPageProps) {
    return (
        <div className="ZapBot_privacy-policy">
            <div className="ZapBot_privacy-policy-text">
                <Trans i18nKey="appcore:ZapBot.privacyPolicy">
                    <h3 className="ZapBot_privacy-policy-title"></h3>
                </Trans>
            </div>

            <div className="ZapBot_privacy-policy-back-button">
                <zaptic-button type="secondary" onClick={onClickBackButton}>
                    <Trans i18nKey="appcore:FileGuideErrorModal.goBack" />
                </zaptic-button>
            </div>
        </div>
    )
}

type CreditsInfoPageProps = {
    creditBalance: Nullable<CreditBalance>
    onClickPrivacyPolicyButton: () => void
}

function CreditsInfoPage({ creditBalance, onClickPrivacyPolicyButton }: CreditsInfoPageProps) {
    return (
        <>
            <div className="ZapBot_credits-info-page">
                <CreditBalanceBox creditBalance={creditBalance} creditsCost={CREDITS_COST.aika} />

                <p className="ZapBot_credits-info-page-text">
                    <Trans i18nKey="appcore:ZapBot.creditsInfoPageText">
                        <span>{{ credits: CREDITS_COST.aika }}</span>
                    </Trans>
                </p>
            </div>

            <Footer onClickPrivacyPolicyButton={onClickPrivacyPolicyButton} showUserTermsLink />
        </>
    )
}
